import { ImagePlus } from "lucide-react";

/**
 * Reusable visual placeholder for any spot that will hold a real project photo.
 * Keeps the layout intact and clearly labels where to drop the final image.
 *
 * Usage:
 *   <PhotoPlaceholder label="Composite Deck — Marietta, GA" sublabel="Add real project photo" aspect="4/3" />
 */
export default function PhotoPlaceholder({
  label,
  sublabel = "Real project photo coming soon",
  aspect = "16/10",
  variant = "dark", // "dark" | "wood" | "cream"
  className = "",
  testId,
  subtle = false, // when true, render only pattern + small corner badge (for hero backdrops)
}) {
  const palette = {
    dark: { bg: "bg-[#141414]", text: "text-white", accent: "text-[#C27A3E]", grid: "bg-white/[0.04]" },
    wood: { bg: "bg-[#99582A]", text: "text-white", accent: "text-[#E8D6C2]", grid: "bg-white/[0.06]" },
    cream: { bg: "bg-[#F1EBE3]", text: "text-[#111]", accent: "text-[#99582A]", grid: "bg-black/[0.04]" },
  }[variant];

  return (
    <div
      data-testid={testId || "photo-placeholder"}
      className={`relative w-full ${palette.bg} ${palette.text} overflow-hidden ${className}`}
      style={{ aspectRatio: aspect }}
    >
      {/* Diagonal stripe pattern */}
      <div
        aria-hidden
        className="absolute inset-0 opacity-60"
        style={{
          backgroundImage:
            "repeating-linear-gradient(45deg, rgba(255,255,255,0.025) 0 12px, transparent 12px 28px)",
        }}
      />
      {!subtle && (
        <>
          {/* Dashed inner frame */}
          <div className="absolute inset-4 md:inset-6 border border-dashed border-current/30" />
          <div className="relative h-full w-full flex flex-col items-center justify-center text-center p-6">
            <div className={`${palette.accent} mb-4 opacity-90`}>
              <ImagePlus size={32} strokeWidth={1.5} />
            </div>
            <p className="font-display text-xl md:text-2xl tracking-tight leading-tight max-w-[80%]">
              {label}
            </p>
            <p
              className={`mt-3 text-[10px] md:text-xs uppercase tracking-[0.25em] font-semibold ${palette.accent}`}
            >
              {sublabel}
            </p>
          </div>
        </>
      )}
      {subtle && (
        <div className="absolute bottom-4 right-4 md:bottom-6 md:right-6 flex items-center gap-2 bg-black/60 backdrop-blur-sm border border-white/15 px-3 py-2 z-10">
          <ImagePlus size={14} strokeWidth={1.75} className={palette.accent} />
          <span className="text-[10px] uppercase tracking-[0.2em] font-semibold text-white/90">
            {label}
          </span>
        </div>
      )}
    </div>
  );
}
